import type { IBetTicketData } from "../logic/Bets/IBetTicketData";

/**
 * Typed wrapper around Web Storage.
 *
 * The two reasons this exists:
 *
 *   1. **Key registry.** Plain `localStorage.getItem("seenVersion")`
 *      scattered across the codebase has no source of truth; one
 *      typo or rename and you silently leak orphan keys. The
 *      registries below (`SessionStorageSchema`, `LocalStorageSchema`)
 *      list every key the app uses and what type each holds, so a
 *      grep on the key name lands on the producer, the consumer,
 *      and this file.
 *
 *   2. **JSON round-tripping.** Every value goes through
 *      `JSON.parse`/`JSON.stringify` automatically; consumers work
 *      in typed-object land and never juggle `try/catch` around
 *      malformed legacy values themselves.
 *
 * Two storage modes:
 *   - `sessionStore` — cleared on tab close. Used for tab-scoped
 *     UX state like the in-progress bet ticket.
 *   - `localStore` — persists across sessions. Used for
 *     cross-session preferences like "have I shown the v2.0.0
 *     What's New modal yet?".
 */

interface SessionStorageSchema {
  // The in-progress ticket the user is building before submitting.
  // Hydrated by useInitializeApp on auth boot so a hard reload
  // mid-ticket doesn't wipe it.
  betTicket: IBetTicketData[];
}

interface LocalStorageSchema {
  // Last APP_VERSION the user has seen the "What's New" modal for.
  // Comparison happens in Page.tsx; mismatch triggers the modal once.
  seenAppVersion: string;
  // Active color theme. Read pre-paint by the bootstrap script in index.html
  // (which parses this same JSON value) and at runtime by `useTheme`. This is
  // the browser-wide *default* (last theme used); a logged-in user's own
  // choice in "br-theme-by-user" overrides it.
  "br-theme": "light" | "dark";
  // Per-user theme overrides, keyed by stringified user id. Lets each account
  // keep its own light/dark choice on this device; applied on auth boot and
  // when the toggle is used while signed in.
  "br-theme-by-user": Record<string, "light" | "dark">;
  // Set only when the toggle is used while signed OUT (auth screens). The
  // next login consumes it (useTheme's `adoptThemeForUser`) and attributes
  // the choice to that user, so an explicit pick on the login page survives
  // the boot-time re-apply of "br-theme-by-user". Carries the toggle time:
  // a marker older than the freshness window is discarded on consumption,
  // so an abandoned signed-out toggle can never override the theme of
  // whoever logs in on this device days later. Absent otherwise.
  "br-theme-preauth": { theme: "light" | "dark"; at: number };
  // Whether this browser has (or recently had) a signed-in session. Written
  // by the auth thunks: `true` on login / successful /me / successful
  // refresh; `false` on logout and on a refresh the server rejected as
  // unauthorized. Read by `shouldAttemptRefresh` (index.tsx) to skip the
  // boot-time /auth/refresh only when we KNOW there is no session (`false`).
  // Absent = unknown → fail-open (attempt the refresh), so sessions created
  // before this key existed still restore.
  "br-had-session": boolean;
  // NOTE: the active UI locale is stored under "br-locale" but is NOT listed
  // here — i18next-browser-languagedetector owns that key and persists a RAW
  // language string (e.g. `en`), bypassing this wrapper's JSON round-tripping.
  // See src/i18n for its detector config.
}

function createTypedStorage<Schema extends object>(backing: Storage) {
  return {
    get<K extends keyof Schema & string>(key: K): Schema[K] | undefined {
      try {
        const raw = backing.getItem(key);
        if (raw === null) return undefined;
        return JSON.parse(raw) as Schema[K];
      } catch {
        // Corrupted payload (e.g. partially-written, manually edited).
        // Treat as absent — let the consumer's default kick in.
        return undefined;
      }
    },
    set<K extends keyof Schema & string>(key: K, value: Schema[K]): void {
      try {
        backing.setItem(key, JSON.stringify(value));
      } catch {
        // Quota exceeded, private-browsing iOS Safari edge cases,
        // etc. We deliberately swallow — storage is best-effort.
      }
    },
    remove<K extends keyof Schema & string>(key: K): void {
      try {
        backing.removeItem(key);
      } catch {
        /* see set() */
      }
    },
  };
}

export const sessionStore = createTypedStorage<SessionStorageSchema>(globalThis.sessionStorage);

export const localStore = createTypedStorage<LocalStorageSchema>(globalThis.localStorage);
